const fixKey = (clbId, columnName) => `${clbId}:${columnName}`;

export const getFailedFixKeys = (errors = []) => new Set(
    errors
        .map((error) => {
            const match = error.match(/^clbId=(\d+)\s+column="([^"]+)"/);
            return match ? `${match[1]}:${match[2]}` : null;
        })
        .filter(Boolean),
);

/**
 * Splits AI fix candidates into bulk-replaceable translation rules and
 * one-off direct cell patches. When the AI provides fixMode, honor it;
 * otherwise fall back to the legacy heuristic (2+ identical replacements).
 */
export const partitionFixesForApplication = (
    candidates,
    stagingItems = [],
    totalRows = stagingItems.length,
) => {
    const translationGroups = new Map();
    const directFixes = [];

    for (const candidate of candidates) {
        const { columnName, originalValue, newValue, clbId, fixMode } = candidate;
        if (fixMode === "direct") {
            directFixes.push(candidate);
            continue;
        }

        const canTranslate = originalValue != null
            && String(originalValue).length > 0
            && clbId != null;

        if (!canTranslate) {
            directFixes.push(candidate);
            continue;
        }

        const groupKey = JSON.stringify([columnName, originalValue, newValue ?? ""]);
        const existing = translationGroups.get(groupKey);
        if (existing) {
            existing.fixes.push(candidate);
        } else {
            translationGroups.set(groupKey, {
                columnName,
                originalValue,
                newValue,
                fixMode,
                fixes: [candidate],
            });
        }
    }

    const translationRules = [];
    const groups = [...translationGroups.values()];
    const chainedColumns = new Set();
    for (const group of groups) {
        if (groups.some((other) =>
            other !== group
            && other.columnName === group.columnName
            && String(group.newValue ?? "") === String(other.originalValue),
        )) {
            chainedColumns.add(group.columnName);
        }
    }

    for (const group of groups) {
        const candidateIds = new Set(group.fixes.map((fix) => String(fix.clbId)));
        const matchingRows = stagingItems.filter((row) => {
            const value = row[group.columnName] ?? row[`CDM_CustomCSV.${group.columnName}`];
            return String(value ?? "") === String(group.originalValue);
        });
        const matchingRowsAreApproved = matchingRows.length === group.fixes.length
            && matchingRows.every((row) => {
                const rowId = row.CLB_ID ?? row.clb_id ?? row["CDM_CustomCSV.CLB_ID"];
                return candidateIds.has(String(rowId));
            });
        const hasCompleteStagingData = stagingItems.length === Number(totalRows);
        const aiChoseTranslation = group.fixes.every((fix) => fix.fixMode === "translation")
            || group.fixMode === "translation";
        const heuristicTranslation = group.fixes.length >= 2
            && group.fixes.every((fix) => !fix.fixMode);

        if (
            (aiChoseTranslation || heuristicTranslation)
            && hasCompleteStagingData
            && matchingRowsAreApproved
            && !chainedColumns.has(group.columnName)
        ) {
            translationRules.push(group);
        } else {
            directFixes.push(...group.fixes);
        }
    }

    return { translationRules, directFixes };
};

export const buildTranslationRuleName = ({ columnName, originalValue, newValue }) =>
    `AI fix: ${columnName} "${originalValue}" → "${newValue ?? ""}"`;

export const buildTranslationActionTooltip = ({ columnName, originalValue, newValue, rowCount }) =>
    `This will create a rule that replaces '${originalValue}' → '${newValue ?? ""}' in column ${columnName} for all ${rowCount} matching rows.`;

export const canRequestAiFixes = ({ aiSuggestionsEnabled, onSuggestFixes }) =>
    aiSuggestionsEnabled && typeof onSuggestFixes === "function";

export const waitForTranslationCompletion = async ({
    translationId,
    listTranslations,
    profileId,
    projectId,
    previousRunAt = null,
    attempts = 15,
    intervalMs = 2000,
}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        const translations = await listTranslations(profileId, projectId);
        const translation = translations?.find((item) => item.id === translationId);
        if (
            translation?.state !== "Processing"
            && translation?.last_run_at
            && translation.last_run_at !== previousRunAt
        ) {
            return translation;
        }
    }
    return null;
};

export const LATE_WATCH_ATTEMPTS = 24;
export const LATE_WATCH_INTERVAL_MS = 5000;

export const watchTranslationUntilComplete = async ({
    translationId,
    listTranslations,
    profileId,
    projectId,
    previousRunAt = null,
    isCancelled = () => false,
    attempts = LATE_WATCH_ATTEMPTS,
    intervalMs = LATE_WATCH_INTERVAL_MS,
}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        if (isCancelled()) return null;
        const result = await waitForTranslationCompletion({
            translationId,
            listTranslations,
            profileId,
            projectId,
            previousRunAt,
            attempts: 1,
            intervalMs,
        }).catch(() => null);
        if (result) return result;
    }
    return null;
};

export { fixKey };
